import React, { useState } from 'react';

import { Box, CircularProgress, Typography, Tooltip, IconButton } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ArrowBack } from '@material-ui/icons';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend } from 'recharts';

const useStyles = makeStyles(() => ({
  backButton: {
    marginRight: 8,
  },
  chartContainer: {
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    padding: '20px',
  },
  chartTitle: {
    alignItems: 'center',
    borderBottom: '1px solid #eaeaea',
    display: 'flex',
    fontSize: 18,
    fontWeight: 600,
    marginBottom: '20px',
    paddingBottom: '10px',
  },
  emptyState: {
    alignItems: 'center',
    color: '#999',
    display: 'flex',
    fontSize: 14,
    height: 300,
    justifyContent: 'center',
  },
  errorState: {
    alignItems: 'center',
    color: '#d32f2f',
    display: 'flex',
    flexDirection: 'column',
    fontSize: 14,
    gap: 10,
    height: 300,
    justifyContent: 'center',
  },
  legendItem: {
    alignItems: 'center',
    display: 'flex',
    fontSize: 12,
    gap: 8,
    marginBottom: 8,
  },
  legendSquare: {
    borderRadius: 2,
    height: 12,
    width: 12,
  },
  loadingContainer: {
    alignItems: 'center',
    display: 'flex',
    height: 300,
    justifyContent: 'center',
  },
  statItem: {
    textAlign: 'center',
  },
  statLabel: {
    color: '#666',
    fontSize: 12,
    marginBottom: 4,
  },
  statValue: {
    color: '#172B4D',
    fontSize: 20,
    fontWeight: 600,
  },
  statsContainer: {
    borderTop: '1px solid #eaeaea',
    display: 'flex',
    gap: 30,
    justifyContent: 'center',
    marginTop: 20,
    paddingTop: 15,
  },
}));

const COLORS = [
  '#0088FE',
  '#00C49F',
  '#FFBB28',
  '#FF8042',
  '#8884D8',
  '#FF6B9D',
  '#C23531',
  '#2F4554',
  '#61A0A8',
  '#D48265',
  '#91C7AE',
  '#749F83',
  '#CA8622',
  '#BDA29A',
  '#6E7074',
];

const TechnologistCategoryChart = ({ data, endDate, error, loading, startDate }) => {
  const classes = useStyles();
  const [selectedCategory, setSelectedCategory] = useState(null);

  if (loading) {
    return (
      <Box className={classes.chartContainer}>
        <Box className={classes.chartTitle}>
          <Typography>Errors by Category</Typography>
        </Box>
        <Box className={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={classes.chartContainer}>
        <Box className={classes.chartTitle}>
          <Typography>Errors by Category</Typography>
        </Box>
        <Box className={classes.errorState}>
          <Typography>Failed to load category data</Typography>
          <Typography variant="caption">{error}</Typography>
        </Box>
      </Box>
    );
  }

  if (!data || !data.data || data.data.length === 0) {
    return (
      <Box className={classes.chartContainer}>
        <Box className={classes.chartTitle}>
          <Typography>Errors by Category</Typography>
        </Box>
        <Box className={classes.emptyState}>
          <Typography>No categorized observations found for this period</Typography>
        </Box>
      </Box>
    );
  }

  const handleCategoryClick = (categoryValue) => {
    const category = data.data.find((cat) => cat.categoryValue === categoryValue);
    if (category && category.subcategories.length > 0) {
      setSelectedCategory(category);
    }
  };

  const handleBackToCategories = () => {
    setSelectedCategory(null);
  };

  const chartData = selectedCategory
    ? selectedCategory.subcategories.map((item) => ({
        name: item.label,
        value: item.count,
      }))
    : data.data.map((item) => ({
        categoryValue: item.categoryValue,
        name: item.category,
        value: item.count,
      }));

  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.65;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    if (percent < 0.05) return null;

    return (
      <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={12} fontWeight={600}>
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  const CustomLegend = () => {
    const itemsToDisplay = selectedCategory ? selectedCategory.subcategories : data.data;

    return (
      <Box>
        {itemsToDisplay.map((item, index) => {
          const displayName = selectedCategory ? item.label : item.category;
          const displayCount = item.count;
          const itemKey = selectedCategory ? item.subcategoryValue : item.categoryValue;
          const hasSubcategories = !selectedCategory && item.subcategories && item.subcategories.length > 0;

          return (
            <Tooltip
              key={itemKey}
              title={
                <Typography variant="body2">
                  {displayName}: {displayCount} ({item.percentage}%)
                </Typography>
              }>
              <Box
                className={classes.legendItem}
                onClick={() => !selectedCategory && hasSubcategories && handleCategoryClick(item.categoryValue)}
                style={{ cursor: !selectedCategory && hasSubcategories ? 'pointer' : 'default' }}>
                <Box className={classes.legendSquare} style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                <Typography>
                  {displayName} ({displayCount})
                </Typography>
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    );
  };

  const handlePieClick = (data, index) => {
    if (!selectedCategory && chartData[index]?.categoryValue) {
      handleCategoryClick(chartData[index].categoryValue);
    }
  };

  const calculateChartHeight = () => {
    const itemCount = chartData.length;
    const baseHeight = 400;
    const legendItemHeight = 28;
    const minHeight = 580;
    const calculatedHeight = baseHeight + Math.max(0, (itemCount - 5) * legendItemHeight);
    return Math.max(minHeight, calculatedHeight);
  };

  return (
    <Box className={classes.chartContainer}>
      <Box className={classes.chartTitle}>
        {selectedCategory && (
          <IconButton size="small" onClick={handleBackToCategories} className={classes.backButton}>
            <ArrowBack />
          </IconButton>
        )}
        <Typography>
          {selectedCategory ? `${selectedCategory.category} - Subcategories` : 'Errors by Category'}
          {startDate && endDate && (
            <Typography component="span" variant="caption" style={{ color: '#666', fontWeight: 400, marginLeft: 8 }}>
              ({startDate} - {endDate})
            </Typography>
          )}
        </Typography>
      </Box>

      <ResponsiveContainer width="100%" height={calculateChartHeight()}>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={renderCustomLabel}
            outerRadius={150}
            fill="#8884d8"
            dataKey="value"
            onClick={handlePieClick}
            style={{ cursor: selectedCategory ? 'default' : 'pointer' }}>
            {chartData.map((entry, index) => (
              <Cell key={`cell-${entry.categoryValue}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Legend content={<CustomLegend />} />
        </PieChart>
      </ResponsiveContainer>

      {!selectedCategory && (
        <Box className={classes.statsContainer}>
          <Box className={classes.statItem}>
            <Typography className={classes.statLabel}>Total Categorized</Typography>
            <Typography className={classes.statValue}>{data.total}</Typography>
          </Box>
          <Box className={classes.statItem}>
            <Typography className={classes.statLabel}>Uncategorized</Typography>
            <Typography className={classes.statValue}>{data.uncategorized}</Typography>
          </Box>
          <Box className={classes.statItem}>
            <Typography className={classes.statLabel}>Categories</Typography>
            <Typography className={classes.statValue}>{data.data.length}</Typography>
          </Box>
        </Box>
      )}

      {selectedCategory && (
        <Box className={classes.statsContainer}>
          <Box className={classes.statItem}>
            <Typography className={classes.statLabel}>Category Total</Typography>
            <Typography className={classes.statValue}>{selectedCategory.count}</Typography>
          </Box>
          <Box className={classes.statItem}>
            <Typography className={classes.statLabel}>Subcategories</Typography>
            <Typography className={classes.statValue}>{selectedCategory.subcategories.length}</Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default TechnologistCategoryChart;
